import {
  Box,
  Text,
  UnorderedList,
  ListItem,
  Heading,
  Flex,
  Divider,
  Button,
  useMediaQuery,
  Tooltip,
  Select,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import FixedTab from './fixedTab';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { lucario } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { MdMenuBook } from 'react-icons/md';
import { CopyIcon } from '@chakra-ui/icons';
import { useRouter } from 'next/router';

export default function MarkdownRender({ version3, version4, version5 }) {
  const [isLargerThan1028] = useMediaQuery('(min-width: 1028px)');
  const [isLargerThan700] = useMediaQuery('(min-width: 700px)');

  const [isOpen, setIsOpen] = useState(false);
  const [smallScreen, setSmallScreen] = useState(false);
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [version, setVersion] = useState('v3');
  const router = useRouter();

  useEffect(() => {
    const { v } = router.query;
    if (v === '3') {
      setVersion('3');
    } else if (v === '4') {
      setVersion('4');
    } else if (v === '5') {
      setVersion('5');
    } else {
      // default to version 3 if no version is specified in the URL
      setVersion('5');
      router.push('/documentation?v=5');
    }
  }, [router.query, router]);

  useEffect(() => {
    if (version4.headers.length !== 0 && version3.headers.length !== 0) {
      if (version === '3') {
        setHeaders(version3.headers);
        setData(version3.newData);
      } else if (version === '4') {
        setHeaders(version4.headers);
        setData(version4.newData);
      } else if (version === '5') {
        setHeaders(version5.headers);
        setData(version5.newData);
      }
    }
  }, [version, version3, version4, version5]);

  const handleVersionChange = (event: any) => {
    setVersion(event.target.value);
    router.push(`/documentation?v=${event.target.value}`);
  };

  const contentPadding = '0 28px';

  const customStyle = {
    border: 'none',
    borderRadius: 0,
    background: '#2a2f45',
    padding: '28px 28px',
    margin: 0,
    fontSize: '12px',
    lineHeight: '1.5',
    fontFamily: 'Consolas, monospace',
  };

  useEffect(() => {
    if (isLargerThan1028) {
      setIsOpen(false);
    }
  }, [isLargerThan1028]);

  useEffect(() => {
    if (isLargerThan700) {
      setSmallScreen(false);
    } else {
      setSmallScreen(true);
    }
  }, [isLargerThan700]);

  let headerCounter = 0;

  if (headers.length !== 0) {
    return (
      <Flex flexDirection={'row'} width={'100%'} fontSize={'14px'}>
        <Flex
          minWidth={'20%'}
          height={'100%'}
          flexDirection={'column'}
          position={!isLargerThan1028 ? 'absolute' : 'relative'}
          left={!isLargerThan1028 ? '-80%' : '0%'}
          transition={'left 0.5s ease-in-out'}
        >
          <Box
            minWidth={'20%'}
            position={'fixed'}
            bg={'#1a1f36'}
            height={'100%'}
          >
            <FixedTab headers={headers} />
          </Box>
        </Flex>
        <Box
          minWidth={'15rem'}
          position={'fixed'}
          bg={'#1a1f36'}
          height={'100%'}
          left={isOpen ? '0' : '-50rem'}
          transition="left 0.5s ease-in-out"
          zIndex={9}
        >
          <FixedTab headers={headers} />
        </Box>
        {!isLargerThan1028 && (
          <Button
            transform={'rotate(90deg)'}
            zIndex={10}
            onClick={() => setIsOpen(!isOpen)}
            position={'fixed'}
            left={-3}
            top={'70%'}
            bgColor={'gray'}
          >
            <MdMenuBook size={'2rem'} />
          </Button>
        )}

        <Box
          width={isLargerThan1028 ? '80%' : '100%'}
          flexDirection="column"
          className="documentation"
          onClick={() => setIsOpen(false)}
        >
          {data.map((item: any, index: number) => {
            const { tag, content, type } = item;

            if (type !== 'h1') {
              let closestH1 = null;
              for (let j = index - 1; j >= 0; j--) {
                if (data[j].type === 'h1') {
                  closestH1 = data[j].id;

                  break;
                }
              }
              item.header = closestH1;
            }

            if (item.content === 'API Reference') {
              item.id = content.replace(/\s/g, '_').toLowerCase();
              headerCounter = 0;

              return (
                <Flex
                  key={index}
                  id={item.id}
                  p={contentPadding}
                  mt={16}
                  flexDirection="row"
                  align={'center'}
                  mb={'21px'}
                  marginRight={smallScreen ? '0%' : '50%'}
                  borderTop={'1px solid #3c4257'}
                  borderBottom={'1px solid #3c4257'}
                  style={!smallScreen ? { clear: 'both' } : { clear: 'none' }}
                >
                  <Heading
                    color={'#c1c9d2'}
                    fontWeight="bold"
                    margin={'0.3em 0'}
                    key={index}
                    fontSize={'25px'}
                  >
                    {item.content}
                  </Heading>
                  <Select
                    ml={3}
                    value={version}
                    onChange={handleVersionChange}
                    color={'#c1c9d2'}
                    width={'6rem'}
                  >
                    {/* <option value={'3'}>V3</option> */}
                    <option value={'5'}>V5</option>
                  </Select>
                </Flex>
              );
            }

            switch (type) {
              case 'p':
                let warning = false;
                if (content.includes('<span class="circle-exclamation">')) {
                  warning = true;
                }
                return (
                  <Text
                    my={4}
                    marginRight={smallScreen ? '0%' : '50%'}
                    dangerouslySetInnerHTML={{ __html: content }}
                    key={index}
                    as="p"
                    p={!warning ? contentPadding : 0}
                  />
                );
              case 'li':
                return (
                  <UnorderedList
                    key={index}
                    marginRight={smallScreen ? '0%' : '50%'}
                    my={4}
                    p={contentPadding}
                  >
                    <ListItem dangerouslySetInnerHTML={{ __html: content }} />
                  </UnorderedList>
                );

              case 'ul':
                return (
                  <UnorderedList
                    key={index}
                    marginRight={smallScreen ? '0%' : '50%'}
                    my={4}
                    p={contentPadding}
                  >
                    <ListItem dangerouslySetInnerHTML={{ __html: content }} />
                  </UnorderedList>
                );

              case 'h1':
                item.id = content.replace(/\s/g, '_').toLowerCase();
                headerCounter = 0;

                return (
                  <Box
                    p={contentPadding}
                    id={item.id}
                    key={index}
                    mt={16}
                    mb={'21px'}
                    marginRight={smallScreen ? '0%' : '50%'}
                    borderTop={'1px solid #3c4257'}
                    borderBottom={'1px solid #3c4257'}
                    style={!smallScreen ? { clear: 'both' } : { clear: 'none' }}
                  >
                    <Heading
                      color={'#c1c9d2'}
                      key={index}
                      fontWeight="bold"
                      margin={'0.3em 0'}
                      fontSize={'25px'}
                    >
                      {content}
                    </Heading>
                  </Box>
                );
              case 'h2':
                headerCounter++;
                item.id = `${item.header
                  .replace(/\s/g, '_')
                  .toLowerCase()}/${content.replace(/\s/g, '_').toLowerCase()}`;
                return (
                  <Box
                    mt={12}
                    id={item.id}
                    key={index}
                    marginRight={smallScreen ? '0%' : '50%'}
                    borderTop={
                      headerCounter === 1 ? 'none' : '1px solid #3c4257'
                    }
                    p={contentPadding}
                    style={!smallScreen ? { clear: 'both' } : { clear: 'none' }}
                  >
                    <Heading
                      color={'#c1c9d2'}
                      key={index}
                      fontSize={'19px'}
                      as="h2"
                      my={3}
                    >
                      {content}
                    </Heading>
                  </Box>
                );
              case 'h3':
                item.id = `${item.header
                  .replace(/\s/g, '_')
                  .toLowerCase()}/${content.replace(/\s/g, '_').toLowerCase()}`;
                return (
                  <Box
                    p={contentPadding}
                    mt={'2.5em'}
                    mb={'0.8em'}
                    id={item.id}
                    key={index}
                    marginRight={smallScreen ? '0%' : '50%'}
                  >
                    <Heading key={index} fontSize={'15px'} color={'#c1c9d2'}>
                      {content}
                    </Heading>
                  </Box>
                );

              case 'blockquote':
                return (
                  <Box
                    key={index}
                    style={
                      !smallScreen
                        ? { float: 'right', clear: 'right', width: '50%' }
                        : { float: 'none', clear: 'none', width: '100%' }
                    }
                  >
                    <Box bg="#3c4257" p={contentPadding}>
                      <Text
                        dangerouslySetInnerHTML={{ __html: content }}
                        p={2}
                        color={'white'}
                      />
                    </Box>
                  </Box>
                );
              case 'code':
                return (
                  <Box
                    key={index}
                    style={
                      !smallScreen
                        ? { float: 'right', clear: 'right', width: '50%' }
                        : { float: 'none', clear: 'none', width: '100%' }
                    }
                  >
                    <Tooltip fontSize={'10px'} label="Copy to clipboard">
                      <Button
                        position={'absolute'}
                        right={0}
                        size={'xs'}
                        variant="ghost"
                        opacity={0.5}
                        _hover={{ bg: 'none', opacity: 0.9 }}
                        onClick={() => navigator.clipboard.writeText(content)}
                      >
                        <CopyIcon />
                      </Button>
                    </Tooltip>
                    <SyntaxHighlighter
                      language={item.info}
                      style={lucario}
                      customStyle={customStyle}
                    >
                      {content}
                    </SyntaxHighlighter>
                  </Box>
                );

              case 'hr':
                return (
                  <Divider
                    borderWidth="0.5px"
                    my={8}
                    color={'white'}
                    key={index}
                    maxW={smallScreen ? '100%' : '50%'}
                    opacity={0.3}
                  />
                );

              default:
                return (
                  <Box
                    key={index}
                    marginRight={smallScreen ? '0%' : '50%'}
                    my={4}
                    p={contentPadding}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                );
            }
          })}
        </Box>
      </Flex>
    );
  }
}
